import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'

const Control = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">Dispatch overview</Typography>
      <Typography>
        The PTS control view is used for live dispatch. It shows duties,
        selected duty details, unassigned journeys, route summaries, journey
        cards, hospital ward context, and a map where route data is available.
      </Typography>
      <Typography>
        Open <DocLink url="/pts/control" text="PTS Control" />.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Duty status</Typography>
      <Typography>
        Duty cards summarise active work with a status derived from what the
        crew are actually doing right now — <strong>Available</strong>,{' '}
        <strong>En Route</strong>, <strong>At Pickup</strong>,{' '}
        <strong>Transporting</strong>, <strong>At Dropoff</strong>,{' '}
        <strong>Crew Logging On</strong>, or <strong>Off Duty</strong> (no
        clocked-on crew) — along with journeys remaining, routes not started,
        patients on board, and the earliest crew finish time. Selecting a duty
        opens more detailed panels for routes, assigned journeys, and journey
        progress.
      </Typography>
      <Typography>
        Where a crew is sharing their location, journey cards show a live{' '}
        <strong>ETA</strong> to the stop the crew is currently heading for,
        computed from the vehicle's streamed position, with the slack against
        the journey's target time. Only the current stop gets one (plus any
        other journeys heading to the same spot) — later stops on a run show
        none rather than a too-early estimate, and nothing is shown while the
        crew are standing at a stop. A position older than five minutes is
        treated as stale, so no ETA appears for a vehicle that's gone quiet.
        The same live estimate feeds the hospital portal's "Crew en route"
        chip and the public tracking page.
      </Typography>
      <Alert severity="info" variant="outlined">
        On the map, a vehicle whose location hasn't updated in the last five
        minutes or more is marked with an amber warning badge — hover it for
        when it last reported. This distinguishes a vehicle that's genuinely
        gone quiet from one that's simply stationary.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Assigning journeys</Typography>
      <Typography>
        Dispatch users can assign unallocated journeys to suitable duties from
        the control interface. This is useful when bookings are created late or
        need reallocating during the day. Candidate duties are marked{' '}
        <strong>Good fit</strong> or <strong>Check first</strong> based on the
        same scoring used on the Planning page (timing, vehicle/mobility fit,
        and existing route compatibility), with any specific warnings shown
        before you confirm.
      </Typography>
      <Typography>
        To move a journey back off a duty, use <strong>Unassign</strong> on
        its journey card in the selected duty's route list — it returns to the
        unassigned list without needing to open the Planning page.
      </Typography>
      <Typography>
        Journey cards also let dispatch progress the workflow on the crew's
        behalf, or record an exception: <strong>No Show</strong> when the
        patient wasn't there, and <strong>Cannot complete</strong> to abort a
        journey — each with its own confirmation. Progressing an arrival step
        from dispatch runs the same 500m location check the crew screens use.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Ending a duty</Typography>
      <Typography>
        <strong>End Duty</strong> on the selected duty closes it down at the
        end of the operational day. If journeys are still unfinished, a
        prompt offers to move them back to the unassigned list before
        ending. You're then asked to review each crew shift's actual finish
        time — with chips flagging finishes beyond the overtime buffer or
        before the early-finish buffer, and an overtime reason where needed —
        so the rota reflects what really happened.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Changing a duty's vehicle</Typography>
      <Typography>
        A duty's vehicle can be set, changed, or removed directly from the
        control screen — useful when a vehicle breaks down or a shift swaps
        vehicles mid-day, without needing to go back to the Planning page.
        Changing the vehicle re-runs the same capacity check used when
        assigning journeys, so a route with a stretcher patient will warn (or
        block, depending on the capacity enforcement setting) if the new
        vehicle can't take it.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Crew location sharing (consent)</Typography>
      <Typography>
        Seeing a vehicle's live position on the map needs the crew member's
        consent — being on the duty is never, on its own, permission to be
        tracked. Each duty card shows a chip per crew member:{' '}
        <strong>sharing</strong> (approved and active), <strong>asked</strong>{' '}
        (request sent, not yet answered), <strong>declined</strong>, or{' '}
        <strong>expired</strong> (an earlier approval has run out).
      </Typography>
      <Typography>
        Click any chip that isn't already <strong>sharing</strong> to send (or
        re-send) a location-sharing request. The crew member answers it in the
        AmbuNet mobile app; once approved, sharing runs until their shift ends
        or they clock out, and their vehicle appears live on the map. Sending
        a request needs the <strong>Co-Ordinator</strong> PTS permission.
      </Typography>
      <Alert severity="info" variant="outlined">
        The request can only be answered in the mobile app — there's no
        approve prompt in the browser crew view. If a chip stays on{' '}
        <strong>asked</strong>, the crew member may be working from a browser
        and hasn't opened the app; duty chat still works as normal for
        finding out where they are.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Live communication</Typography>
      <Typography>
        Dispatchers can use duty chat to communicate with crew assigned to PTS
        work. Crew messages and control messages stay with the duty so the
        conversation remains available during the operational day.
      </Typography>
      <Typography>
        The dispatch screen also makes the control room available to hospital
        portal users. When a portal user asks for help, dispatchers receive a
        portal chat request, accept it from the control screen, and then
        exchange messages in the hospital portal chat window.
      </Typography>
      <Alert severity="info" variant="outlined">
        Portal users can only start live chat while a dispatcher is active on
        the PTS control screen.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Crew view</Typography>
      <Typography>
        The crew-facing PTS route is available from <DocLink url="/pts" text="PTS" />
        and is separate from the dispatch control page. It gives crew members
        a phone-friendly view of their own duty: today's journeys, one clear
        next action, arrival confirmation, sat-nav handoff, delay reporting,
        and duty chat with control room. See{' '}
        <DocLink
          url="/patient-transport/crew-view"
          text="Crew View"
          docsPage
        />{' '}
        for the full guide, or{' '}
        <DocLink
          url="/patient-transport/mobile-app"
          text="Mobile App"
          docsPage
        />{' '}
        for the native phone app equivalent — either way, vehicle location on
        this screen's map comes from whichever one the crew member is using.
      </Typography>
      <Alert severity="info" variant="outlined">
        Patient Transport is an enterprise module. The crew view itself needs
        no PTS permission — it's open to every employee; this dispatch screen
        needs the <strong>Co-Ordinator</strong> permission.
      </Alert>
    </Stack>
  </Stack>
)

export default Control
